import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import api from '../api';
import LoadingSpinner from '../components/LoadingSpinner';

function AnimatedCheckmark() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" className="checkmark-animated" />
    </svg>
  );
}

function CountUpAmount({ amount }) {
  const [displayAmount, setDisplayAmount] = useState(0);
  const frameRef = useRef(null);

  useEffect(() => {
    if (amount <= 0) { setDisplayAmount(0); return; }
    const duration = 600;
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayAmount(Math.round(amount * eased));
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      }
    };
    frameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameRef.current);
  }, [amount]);

  return <>{displayAmount.toLocaleString()}원</>;
}

function Confetti() {
  const colors = ['#BF9B30', '#00BF40', '#FF9500', '#F04452', '#A855F7', '#06B6D4'];
  const pieces = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    color: colors[Math.floor(Math.random() * colors.length)],
    duration: `${2 + Math.random() * 2}s`,
    delay: `${Math.random() * 1.5}s`,
    size: `${6 + Math.random() * 8}px`,
  }));
  return (
    <div className="confetti-container">
      {pieces.map((p) => (
        <div key={p.id} className="confetti-piece" style={{ left: p.left, background: p.color, width: p.size, height: p.size, animationDuration: p.duration, animationDelay: p.delay, borderRadius: Math.random() > 0.5 ? '50%' : '2px' }} />
      ))}
    </div>
  );
}

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [countdown, setCountdown] = useState(30);
  const [totalAmount, setTotalAmount] = useState(0);
  const countdownRef = useRef(null);
  const ctxRef = useRef(null);

  useEffect(() => {
    const confirm = async () => {
      const locationState = location.state;
      if (locationState?.amount) {
        setTotalAmount(locationState.amount);
        ctxRef.current = locationState;
        setStatus('success');
        return;
      }

      const paymentId = searchParams.get('paymentId');
      const txId = searchParams.get('txId');
      const code = searchParams.get('code');

      if (code) {
        const message = searchParams.get('message') || '결제가 취소되었습니다.';
        navigate(`/payment/fail?message=${encodeURIComponent(message)}`, { replace: true });
        return;
      }

      if (!paymentId) {
        setStatus('error');
        return;
      }

      const rawCtx = localStorage.getItem('portone_payment_ctx');
      let ctx = {};
      try { ctx = rawCtx ? JSON.parse(rawCtx) : {}; } catch {}
      ctxRef.current = ctx;
      setTotalAmount(ctx.amount || 0);

      try {
        await api.post('/payments/portone-confirm', {
          payment_id: paymentId,
          portone_tx_id: txId,
          amount: ctx.amount,
        });
        localStorage.removeItem('portone_payment_ctx');
        setStatus('success');
      } catch {
        setStatus('error');
      }
    };
    confirm();
  }, []);

  useEffect(() => {
    if (status !== 'success') return;
    setCountdown(30);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          doReset();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [status]);

  const doReset = () => {
    const ctx = ctxRef.current;
    if (ctx?.storeId && ctx?.tableId) {
      api.post(`/stores/${ctx.storeId}/tables/${ctx.tableId}/checkout`).catch(() => {});
      navigate(`/table/${ctx.storeId}/${ctx.tableId}`);
    } else {
      navigate('/');
    }
  };

  if (status === 'loading') return <LoadingSpinner fullPage />;

  if (status === 'error') {
    return (
      <div className="page" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center', gap: 12 }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--danger-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--danger)" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.5px' }}>결제 확인 실패</div>
        <div style={{ fontSize: 14, color: 'var(--text-tertiary)' }}>잠시 후 다시 시도해주세요.</div>
        <button className="btn btn-primary btn-lg" style={{ marginTop: 8 }} onClick={() => navigate(-1)}>돌아가기</button>
      </div>
    );
  }

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 32, textAlign: 'center', gap: 12 }}>
      <Confetti />
      <div className="animate-scale-in" style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <AnimatedCheckmark />
      </div>
      <div className="animate-fade-in-up" style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.5px', animationDelay: '200ms' }}>결제 완료!</div>
      <div className="animate-fade-in-up" style={{ fontSize: 15, color: 'var(--text-tertiary)', lineHeight: 1.7, animationDelay: '300ms' }}>
        식사 즐거우셨나요?<br/>또 방문해주세요
      </div>
      <div className="animate-scale-in" style={{ background: 'var(--primary-light)', borderRadius: 'var(--radius-lg)', padding: '20px 40px', marginTop: 8, animationDelay: '400ms' }}>
        <div style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 600, marginBottom: 4 }}>총 결제 금액</div>
        <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--primary)', letterSpacing: '-0.5px' }}><CountUpAmount amount={totalAmount} /></div>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-quaternary)', marginTop: 8 }}>
        {countdown}초 후 자동으로 초기화됩니다
      </div>
      <button
        className="btn btn-primary btn-lg"
        style={{ marginTop: 8 }}
        onClick={() => { clearInterval(countdownRef.current); doReset(); }}
      >
        메뉴로 돌아가기
      </button>
    </div>
  );
}
